/**
 * Swish Court Builder - Tooltips
 * - Reads tooltip copy from `script[data-swishcb-tooltips]`
 * - Shows a tooltip bubble when hovering/focusing any `[data-swishcb-tooltip]` trigger
 */
(function () {
  'use strict';

  const TRIGGER_SELECTOR = '[data-swishcb-tooltip]';
  const TOOLTIP_ID = 'swishcb-tooltip-bubble';

  /** @type {HTMLElement|null} */
  let bubble = null;
  /** @type {HTMLElement|null} */
  let activeTrigger = null;
  let pinnedTrigger = null;

  function safeJsonParse(text) {
    try {
      return JSON.parse(text);
    } catch (e) {
      return null;
    }
  }

  function getTooltipMap(root) {
    const el =
      (root && root.querySelector && root.querySelector('script[data-swishcb-tooltips]')) ||
      document.querySelector('script[data-swishcb-tooltips]');
    if (!el) return null;
    const map = safeJsonParse(el.textContent || '');
    return map && typeof map === 'object' ? map : null;
  }

  function ensureBubble() {
    if (bubble && document.body.contains(bubble)) return bubble;
    bubble = document.getElementById(TOOLTIP_ID);
    if (bubble) return bubble;

    const div = document.createElement('div');
    div.id = TOOLTIP_ID;
    div.className = 'swishcb__tooltip';
    div.setAttribute('role', 'tooltip');
    div.hidden = true;
    document.body.appendChild(div);
    bubble = div;
    return bubble;
  }

  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  function positionBubble(triggerEl) {
    if (!bubble || !triggerEl) return;
    const rect = triggerEl.getBoundingClientRect();

    const prevHidden = bubble.hidden;
    bubble.hidden = false;
    bubble.style.left = '0px';
    bubble.style.top = '0px';
    bubble.style.maxWidth = '';

    const b = bubble.getBoundingClientRect();
    const margin = 10;
    const viewportW = Math.max(1, document.documentElement.clientWidth || window.innerWidth || 0);
    const viewportH = Math.max(320, window.innerHeight || 0);

    const maxW = clamp(Math.floor(viewportW * 0.86), 220, 360);
    bubble.style.maxWidth = maxW + 'px';

    const b2 = bubble.getBoundingClientRect();
    const bubbleW = b2.width || b.width;
    const bubbleH = b2.height || b.height;

    const aboveTop = rect.top - bubbleH - 8;
    const belowTop = rect.bottom + 8;
    const top =
      aboveTop >= margin
        ? aboveTop
        : clamp(belowTop, margin, viewportH - bubbleH - margin);

    const centeredLeft = rect.left + rect.width / 2 - bubbleW / 2;
    const left = clamp(centeredLeft, margin, viewportW - bubbleW - margin);

    bubble.style.left = left + 'px';
    bubble.style.top = top + 'px';
    bubble.hidden = prevHidden;
  }

  function getTextForKey(map, key) {
    if (!map || !key) return null;
    const value = map[key];
    return typeof value === 'string' && value.trim() ? value : null;
  }

  const COURT_TOOLTIP_META = {
    fiba: { sport: 'Basketball', basketball: true, main: 'main court', secondary: 'key' },
    nba: { sport: 'Basketball', basketball: true, main: 'main court', secondary: 'key' },
    ncaa: { sport: 'Basketball', basketball: true, main: 'main court', secondary: 'key' },
    usahs: { sport: 'Basketball', basketball: true, main: 'main court', secondary: 'key' },
    tennis: { sport: 'Tennis', main: 'backcourt and doubles alley areas', secondary: 'service court' },
    pickleball: { sport: 'Pickleball', main: 'service court', secondary: 'kitchen' },
    padel: { sport: 'Padel', main: 'backcourt', secondary: 'service box' },
    volleyball: { sport: 'Volleyball', main: 'back zone', secondary: 'front zone' },
    shuffleboard: { sport: 'Shuffleboard', main: 'scoring area', secondary: 'detail area' },
    futsal: { sport: 'Futsal', main: 'court half', secondary: 'penalty area' },
    netball: { sport: 'Netball', main: 'third', secondary: 'goal circle' },
    racquetball: { sport: 'Racquetball', main: 'court', secondary: 'service zone' }
  };

  const EXTRA_TOOLTIP_LABELS = {
    fiba3: 'FIBA 3-point',
    nba3: 'NBA 3-point',
    ncaa3: 'NCAA 3-point',
    usahs3: 'USA High School 3-point',
    tennis: 'Tennis',
    pickleball: 'Pickleball',
    padel: 'Padel',
    volleyball: 'Volleyball',
    shuffleboard: 'Shuffleboard',
    cornhole: 'Cornhole',
    futsal: 'Futsal',
    netball: 'Netball',
    racquetball: 'Racquetball'
  };

  function normalizeKey(value) {
    return String(value || '').trim().toLowerCase();
  }

  function getSelectedText(select) {
    if (!select || !select.options) return '';
    const option = select.options[select.selectedIndex];
    return option ? String(option.textContent || '').trim() : '';
  }

  function getCourtContext(root) {
    const designSelect =
      (root && root.querySelector && root.querySelector('#court-xxx')) ||
      document.querySelector('#court-xxx');
    const courtType = normalizeKey(designSelect && designSelect.value);
    const meta = COURT_TOOLTIP_META[courtType] || {
      sport: getSelectedText(designSelect) || 'selected sport',
      main: 'main surface',
      secondary: 'secondary area'
    };

    const hoopSelect =
      (root && root.querySelector && root.querySelector('#swishcb-hoops-mode')) ||
      document.querySelector('#swishcb-hoops-mode');
    const hoopMode = parseInt(hoopSelect && hoopSelect.value, 10);
    const sideHoops = ['left', 'right', 'top', 'bottom'].some((side) => {
      const el =
        (root && root.querySelector && root.querySelector('#swishcb-' + side + '-hoop')) ||
        document.querySelector('#swishcb-' + side + '-hoop');
      return !!(el && el.checked);
    });
    const hasHoops = (Number.isFinite(hoopMode) && hoopMode > 0) || sideHoops;

    const extraSports = [];
    const scope = root && root.querySelectorAll ? root : document;
    try {
      scope.querySelectorAll('[data-extra-toggle]').forEach((input) => {
        if (!input || !input.checked || !input.dataset) return;
        const id = normalizeKey(input.dataset.extraToggle);
        if (id) extraSports.push(id);
      });
    } catch (_) {}

    return {
      courtType,
      meta,
      hasHoops,
      extraSports,
      isMultiSport: extraSports.length > 0 || (!meta.basketball && hasHoops)
    };
  }

  function getExtraSportLabel(triggerEl) {
    const card = triggerEl && triggerEl.closest ? triggerEl.closest('[data-extra-card]') : null;
    const id = normalizeKey(card && card.dataset ? card.dataset.extraCard : '');
    if (id && EXTRA_TOOLTIP_LABELS[id]) return EXTRA_TOOLTIP_LABELS[id];
    if (id) return id.replace(/(^|-)([a-z])/g, (_match, dash, chr) => (dash ? ' ' : '') + chr.toUpperCase());
    return 'this sport';
  }

  function getSecondaryPlural(ctx) {
    const term = String((ctx && ctx.meta && ctx.meta.secondary) || 'secondary area');
    if (/key$/i.test(term)) return term + 's';
    if (/box$/i.test(term)) return term + 'es';
    if (/y$/i.test(term)) return term.replace(/y$/i, 'ies');
    if (/s$/i.test(term)) return term;
    return term + 's';
  }

  function getContextualTooltipText(key, root, triggerEl) {
    if (!key) return null;

    if (key.indexOf('extra.') === 0) {
      const sport = getExtraSportLabel(triggerEl);
      if (key === 'extra.line_color') return 'Choose the ' + sport + ' line color for this added sport overlay.';
      if (key === 'extra.scale') return 'Scale the ' + sport + ' overlay up or down.';
      if (key === 'extra.rotate') return 'Rotate the ' + sport + ' overlay.';
      if (key === 'extra.offset_x') return 'Move the ' + sport + ' overlay left or right.';
      if (key === 'extra.offset_y') return 'Move the ' + sport + ' overlay up or down.';
      return null;
    }

    const ctx = getCourtContext(root);
    const sport = ctx.meta.sport || 'selected sport';
    const main = ctx.meta.main || 'main surface';
    const secondary = ctx.meta.secondary || 'secondary area';
    const secondaryPlural = getSecondaryPlural(ctx);
    const multiSuffix = ctx.isMultiSport
      ? ' Use More Options for each sport, added-sport areas, lines, and basketball hoop details when they are included.'
      : '';

    switch (key) {
      case 'court.add_more_sports':
        return ctx.isMultiSport
          ? 'Add or adjust extra sport line overlays for this multi-sport court.'
          : 'Add extra sport line overlays on top of the current ' + sport + ' court.';
      case 'court.center_circle_diameter':
        return ctx.meta.basketball
          ? 'Set the visible outside-edge diameter of the Basketball center circle.'
          : 'Set the visible outside-edge diameter for this sport-specific center marking, when the selected court uses one.';
      case 'court.three_point_distance':
        return ctx.meta.basketball
          ? 'Set the Basketball 3-point distance from the rim to the arc.'
          : 'Set the Basketball 3-point distance for hoop markings on this multi-sport court.';
      case 'court.distance_arrows':
        if (ctx.isMultiSport) return 'Show or hide total dimensions and sport-specific measurement overlays for this multi-sport court.';
        if (ctx.meta.basketball) return 'Show or hide measurement overlays for total dimensions, keys, and 3-point lines.';
        return 'Show or hide measurement overlays for total dimensions and ' + sport + ' markings.';
      case 'court.hoops':
        return ctx.meta.basketball
          ? 'Choose how many Basketball hoops are included in the court design.'
          : 'Add Basketball hoops to this ' + sport + ' court to create a multi-sport layout.';
      case 'court.key_width':
        return 'Adjust the width of the ' + secondary + ' for the selected ' + sport + ' court.';
      case 'court.key_length':
        return 'Adjust the length of the ' + secondary + ' for the selected ' + sport + ' court.';
      case 'court.key_tile_fit':
        return 'Choose how the ' + secondary + ' should fit to the tile grid for the selected ' + sport + ' court.';
      case 'court.tile_edges':
        return 'Choose which outside tile edge strips are shown around the court.';
      case 'colors.main_court':
        return ctx.isMultiSport
          ? 'Choose the main surface color for this multi-sport court. This controls the primary ' + sport + ' ' + main + '.'
          : 'Choose the ' + main + ' color for the ' + sport + ' court.';
      case 'colors.key':
        return ctx.isMultiSport
          ? 'Choose the ' + secondary + ' color for the primary ' + sport + ' court.' + multiSuffix
          : 'Choose the ' + secondary + ' color for the ' + sport + ' court.';
      case 'colors.lines':
        return ctx.isMultiSport
          ? 'Choose the default line color for this multi-sport court. Added sports can also have their own line colors.'
          : 'Choose the ' + sport + ' line color.';
      case 'colors.border':
        return ctx.isMultiSport
          ? 'Choose the outer border color for this multi-sport court.'
          : 'Choose the outer border color for the ' + sport + ' court.';
      case 'colors.edge':
        return 'Choose the outside tile edge color.';
      case 'colors.more_options':
        return ctx.isMultiSport
          ? 'Open advanced color controls for this multi-sport court, including ' + sport + ' details, added sports, borders, and basketball hoop details when they are included.'
          : 'Open advanced color controls for ' + sport + ' areas, ' + secondaryPlural + ', lines, and borders.';
      default:
        return null;
    }
  }

  function showTooltip(root, triggerEl) {
    const map = getTooltipMap(root);
    const key = triggerEl && triggerEl.dataset ? triggerEl.dataset.swishcbTooltip : '';
    const text =
      getContextualTooltipText(key, root, triggerEl) ||
      getTextForKey(map, key) ||
      (triggerEl && triggerEl.dataset ? triggerEl.dataset.swishcbTooltipFallback || '' : '') ||
      null;
    if (!text) return;

    if (activeTrigger && activeTrigger !== triggerEl) hideTooltip(activeTrigger);

    const tip = ensureBubble();
    tip.textContent = text;
    tip.hidden = false;

    activeTrigger = triggerEl;
    try {
      triggerEl.setAttribute('aria-describedby', TOOLTIP_ID);
    } catch (e) {}

    positionBubble(triggerEl);
  }

  function hideTooltip(triggerEl) {
    if (!bubble) return;
    bubble.hidden = true;
    bubble.textContent = '';

    if (triggerEl) {
      try {
        triggerEl.removeAttribute('aria-describedby');
      } catch (e) {}
    }
    activeTrigger = null;
    pinnedTrigger = null;
  }

  function closestTrigger(node) {
    if (!node || node === document) return null;
    if (node.closest) return node.closest(TRIGGER_SELECTOR);
    return null;
  }

  function isClickToggleTrigger(triggerEl) {
    return !!(
      triggerEl &&
      triggerEl.classList &&
      (triggerEl.classList.contains('swishcb__tt--label') || triggerEl.matches('button[data-swishcb-tooltip-toggle]'))
    );
  }

  function attachToRoot(root) {
    if (!root || root.__swishcbTooltipsAttached) return;
    root.__swishcbTooltipsAttached = true;

    root.addEventListener('pointerover', (e) => {
      const t = closestTrigger(e.target);
      if (!t) return;
      showTooltip(root, t);
    });

    root.addEventListener('pointerout', (e) => {
      const t = closestTrigger(e.target);
      if (!t) return;
      const to = e.relatedTarget;
      if (to && t.contains(to)) return;
      if (pinnedTrigger === t) return;
      hideTooltip(t);
    });

    root.addEventListener('focusin', (e) => {
      const t = closestTrigger(e.target);
      if (!t) return;
      showTooltip(root, t);
    });

    root.addEventListener('focusout', (e) => {
      const t = closestTrigger(e.target);
      if (!t) return;
      hideTooltip(t);
    });

    root.addEventListener('click', (e) => {
      const t = closestTrigger(e.target);
      if (!t || !isClickToggleTrigger(t)) return;
      try { e.preventDefault(); } catch (_) {}
      try { e.stopPropagation(); } catch (_) {}
      try { e.stopImmediatePropagation(); } catch (_) {}

      // Hover/focus may already have opened an icon tooltip before its first
      // click. Pin it on that click, rather than immediately hiding it.
      if (t.matches('button[data-swishcb-tooltip-toggle]')) {
        if (pinnedTrigger === t && bubble && !bubble.hidden) hideTooltip(t);
        else { showTooltip(root, t); pinnedTrigger = t; }
        return;
      }

      if (activeTrigger === t && bubble && !bubble.hidden) {
        hideTooltip(t);
        return;
      }

      showTooltip(root, t);
    });

    root.addEventListener('keydown', (e) => {
      const t = closestTrigger(e.target);
      if (!t || !isClickToggleTrigger(t)) return;
      // Native buttons dispatch click for Enter and Space themselves.
      if (t.matches('button[data-swishcb-tooltip-toggle]')) return;
      const key = e.key || '';
      if (key !== 'Enter' && key !== ' ') return;
      try { e.preventDefault(); } catch (_) {}
      try { e.stopPropagation(); } catch (_) {}
      try { e.stopImmediatePropagation(); } catch (_) {}

      if (activeTrigger === t && bubble && !bubble.hidden) {
        hideTooltip(t);
        return;
      }

      showTooltip(root, t);
    });

    const onReposition = () => {
      if (!bubble || bubble.hidden || !activeTrigger) return;
      positionBubble(activeTrigger);
      bubble.hidden = false;
    };

    window.addEventListener('scroll', onReposition, { passive: true });
    window.addEventListener('resize', onReposition, { passive: true });
  }

  function boot() {
    const roots = document.querySelectorAll('.swishcb');
    roots.forEach((root) => attachToRoot(root));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('click', (e) => {
    const t = closestTrigger(e.target);
    if (t) return;
    if (!bubble || bubble.hidden || !activeTrigger) return;
    hideTooltip(activeTrigger);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && activeTrigger) hideTooltip(activeTrigger);
  });
})();
